import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import StatCard from "../components/common/StatCard";
import Loader from "../components/common/Loader";
import ErrorState from "../components/common/ErrorState";
import EmptyState from "../components/common/EmptyState";
import Badge from "../components/common/Badge";
import campusService from "../services/campusService";
import { formatDate } from "../utils/format";

export default function CampusDashboard() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  function load() {
    setState("loading");
    campusService
      .dashboard()
      .then((res) => {
        setData(res.data);
        setState("ready");
      })
      .catch(() => setState("error"));
  }

  useEffect(load, []);

  if (state === "loading") return <div className="pm-page"><Loader label="Loading dashboard" /></div>;
  if (state === "error") return <div className="pm-page"><ErrorState onRetry={load} message="Couldn't load dashboard data." /></div>;

  const { totals, recentApplications, studentsByUniversity, studentsBySpecialisation, openCampuses } = data;
  const maxUni = Math.max(1, ...studentsByUniversity.map((r) => r.count));
  const maxSpec = Math.max(1, ...studentsBySpecialisation.map((r) => r.count));

  return (
    <div className="pm-page">
      <header className="pm-page__header">
        <div>
          <span className="pm-eyebrow">Overview</span>
          <h1>Campus dashboard</h1>
        </div>
      </header>

      <div className="pm-stat-grid">
        <StatCard label="Total universities" value={totals.total_universities} index={0} />
        <StatCard label="Active universities" value={totals.active_universities} index={1} />
        <StatCard label="Total students" value={totals.total_students} index={2} />
        <StatCard label="Active students" value={totals.active_students} index={3} />
      </div>

      <div className="pm-dash-grid">
        <motion.div
          className="pm-card"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h3>Students by university</h3>
          {studentsByUniversity.length === 0 ? (
            <EmptyState title="No applications yet" />
          ) : (
            <div className="pm-bar-list">
              {studentsByUniversity.map((row) => (
                <div className="pm-bar-row" key={row.name}>
                  <span className="pm-bar-row__label">{row.name}</span>
                  <div className="pm-bar-row__track">
                    <motion.div
                      className="pm-bar-row__fill"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(row.count / maxUni) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <span className="pm-bar-row__count">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          className="pm-card"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.08 }}
        >
          <h3>Students by specialisation</h3>
          {studentsBySpecialisation.length === 0 ? (
            <EmptyState title="No applications yet" />
          ) : (
            <div className="pm-bar-list">
              {studentsBySpecialisation.map((row) => (
                <div className="pm-bar-row" key={row.name}>
                  <span className="pm-bar-row__label">{row.name}</span>
                  <div className="pm-bar-row__track pm-bar-row__track--alt">
                    <motion.div
                      className="pm-bar-row__fill pm-bar-row__fill--alt"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${(row.count / maxSpec) * 100}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <span className="pm-bar-row__count">{row.count}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      <div className="pm-dash-grid">
        <motion.div
          className="pm-card"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <div className="pm-card__header">
            <h3>Recent applications</h3>
            <Link to="/admin/students" className="pm-table__link">View all →</Link>
          </div>
          {recentApplications.length === 0 ? (
            <EmptyState title="No applications yet" description="They'll show up here as students apply." />
          ) : (
            <ul className="pm-recent-list">
              {recentApplications.map((a) => (
                <li key={a.id}>
                  <div>
                    <strong>{a.name}</strong>
                    <span>{a.university_name} · {a.specialisation || "—"}</span>
                  </div>
                  <div className="pm-recent-list__meta">
                    <Badge active={!!a.is_active} />
                    <span>{formatDate(a.applied_date)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </motion.div>

        <motion.div
          className="pm-card"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.08 }}
        >
          <div className="pm-card__header">
            <h3>Open hiring windows</h3>
            <Link to="/admin/universities" className="pm-table__link">Manage →</Link>
          </div>
          {openCampuses.length === 0 ? (
            <EmptyState title="No open campuses" description="Activate a campus to start receiving applications." />
          ) : (
            <ul className="pm-recent-list">
              {openCampuses.map((c) => (
                <li key={c.id}>
                  <div>
                    <strong>{c.name}</strong>
                    <span>{c.location || "—"} · {c.type_of_hire || "—"}</span>
                  </div>
                  <div className="pm-recent-list__meta">
                    <span>{formatDate(c.start_date)} – {formatDate(c.end_date)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </motion.div>
      </div>
    </div>
  );
}
